// The active/passive sleeve roster, and the only place a sleeve can be confirmed.
//
// WHAT A SLEEVE IS. A pairing: one active holding we own, and the passive instrument that is
// the honest alternative to owning it. The engine multiplies the sleeve's exposure by the
// return differential between the two legs to produce active_contribution — 45 IR cells sit
// behind these fourteen rows. An unconfirmed sleeve is not used.
//
// WHY THERE IS NO "CONFIRM ALL" BUTTON. Confirming is not administrative tidying: it is a
// named human agreeing that this fund is the active expression of that benchmark, recorded as
// confirmed_by = auth.uid() against a figure that reaches a retail newsletter. A bulk button
// would make fourteen judgements into one click and would record fourteen signatures for one
// act of attention. The legs are therefore shown inline on every row, so the thing being
// agreed to is on screen next to the button that agrees to it.
//
// WHY IT CANNOT BE DONE FROM A SERVER SESSION. See useSleeves.ts — confirm_sleeve() refuses a
// NULL auth.uid() outright, and neither a service-role client nor the Supabase SQL editor has
// one. This tab is not a convenience; it is the only route that exists.

import { useOutletContext } from "react-router-dom";
import { EmptyState, ErrorState } from "@/components/research-hub";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  useSleeves,
  useConfirmSleeve,
  sleeveBlockReason,
  type Sleeve,
  type SleeveLeg,
} from "@/hooks/investment-reporting/useSleeves";
import type { CycleContext } from "./ReportingCycleLayout";

export default function SleevesTab() {
  const { canSign } = useOutletContext<CycleContext>();
  const { data: sleeves, isLoading, error } = useSleeves();
  const confirm = useConfirmSleeve();

  if (isLoading) {
    return <p className="font-body text-sm text-muted-foreground">Loading sleeves…</p>;
  }
  if (error) {
    // The message is SHOWN rather than swallowed behind a generic ErrorState title: the two
    // likely causes — 0003v not applied, and the reader lacking Investment/Operations/admin —
    // need opposite fixes, and "we couldn't load this" sends people to the wrong one.
    return <ErrorState title={`Could not read the sleeve roster: ${(error as Error).message}`} />;
  }
  if (!sleeves || sleeves.length === 0) {
    return (
      <EmptyState
        title="No sleeves defined."
        body="reporting_sleeves is empty, or your account cannot read it. Sleeves arrive with migration 0003v; the read policy is Investment, Operations or an admin."
      />
    );
  }

  const unconfirmed = sleeves.filter((s) => !s.confirmed_at).length;

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <p className="font-body text-sm">
          {unconfirmed === 0 ? (
            <>All {sleeves.length} sleeves are confirmed.</>
          ) : (
            <>
              <strong>{unconfirmed}</strong> of {sleeves.length} sleeves are unconfirmed. The
              engine does not use an unconfirmed sleeve, so its contribution cells publish{" "}
              <code className="text-xs">not_found</code> rather than a number.
            </>
          )}
        </p>
        {!canSign && (
          <p className="font-body text-xs text-muted-foreground">
            The roster is read-only for you. Confirming a sleeve is for the Investment team, the
            Operations team, or an admin — and it records your name against the pairing.
          </p>
        )}
      </div>

      {confirm.isError && (
        <p className="font-body text-sm text-destructive">
          {(confirm.error as Error).message}
        </p>
      )}

      <div className="space-y-2">
        {sleeves.map((s) => (
          <SleeveRow
            key={s.sleeve}
            sleeve={s}
            canSign={canSign}
            confirming={confirm.isPending && confirm.variables?.sleeve === s.sleeve}
            onConfirm={() => confirm.mutate({ sleeve: s.sleeve })}
          />
        ))}
      </div>
    </div>
  );
}

function SleeveRow({
  sleeve: s,
  canSign,
  confirming,
  onConfirm,
}: {
  sleeve: Sleeve;
  canSign: boolean;
  confirming: boolean;
  onConfirm: () => void;
}) {
  const blocked = sleeveBlockReason(s);
  const confirmed = Boolean(s.confirmed_at);
  const active = s.legs.filter((l) => l.side === "active");
  const benchmark = s.legs.filter((l) => l.side === "benchmark");

  return (
    <div
      className={cn(
        "rounded-lg border p-3 space-y-2",
        confirmed ? "bg-muted/30" : "bg-background",
      )}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="space-y-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-body text-sm font-medium">{s.label}</span>
            {/* selection vs allocation is not cosmetic. A selection sleeve credits a MANAGER;
                an allocation sleeve is a house TILT and crediting it as manager alpha would
                misattribute it in a document a named human signs. */}
            <Badge variant={s.kind === "selection" ? "default" : "secondary"}>
              {s.kind === "selection" ? "manager selection" : "allocation tilt"}
            </Badge>
            <code className="text-xs text-muted-foreground">{s.sleeve}</code>
          </div>
          <LegList label="Active" legs={active} />
          <LegList label="Passive" legs={benchmark} />
          {s.note && (
            <p className="font-body text-xs text-muted-foreground">{s.note}</p>
          )}
        </div>

        <div className="shrink-0 text-right space-y-1">
          {confirmed ? (
            <>
              <Badge variant="outline" className="border-green-600 text-green-700">
                Confirmed
              </Badge>
              <p className="font-body text-xs text-muted-foreground">
                {new Date(s.confirmed_at as string).toLocaleString("en-GB")}
              </p>
            </>
          ) : (
            <Button
              size="sm"
              disabled={!canSign || Boolean(blocked) || confirming}
              onClick={onConfirm}
            >
              {confirming ? "Confirming…" : "Confirm"}
            </Button>
          )}
        </div>
      </div>

      {blocked && !confirmed && (
        <p className="font-body text-xs text-destructive">
          Cannot be confirmed: {blocked}.
        </p>
      )}
    </div>
  );
}

function LegList({ label, legs }: { label: string; legs: SleeveLeg[] }) {
  if (legs.length === 0) {
    return (
      <p className="font-body text-xs text-destructive">
        {label}: none
      </p>
    );
  }
  return (
    <p className="font-body text-xs text-muted-foreground">
      <span className="font-medium text-foreground">{label}:</span>{" "}
      {legs.map((l, i) => (
        <span key={`${l.side}-${l.return_ref}-${i}`}>
          {i > 0 && " + "}
          {l.return_ref}
          {/* An active leg with no holding_ref has no weight and cannot contribute — the RPC
              refuses the whole sleeve for it, so it is called out here rather than left to
              read as a normal leg. */}
          {l.side === "active" && !l.holding_ref && (
            <span className="text-destructive"> (no holding_ref)</span>
          )}
          {l.fixed_weight != null && (
            <span> @ {Number(l.fixed_weight)}%</span>
          )}
        </span>
      ))}
    </p>
  );
}
